import { networkRoundTrip, type PingProbe, type ResourceStamps } from "./ping.ts";

/**
 * `/ping`'s network probe on the page (ADR-068's amendment "`/ping` measures the network", item 2): a `GET /ping`
 * to the gateway, timed from its Resource Timing entry rather than on the main thread, so a frame's work never
 * lands in the round trip. Each request's address is unique to the page (`/ping?n=<count>`), so its entry is told
 * apart from any other; the entry is taken from a `PerformanceObserver`, which is told of every entry even once the
 * timeline's buffer (250 by default) is full, and the buffer is never cleared.
 */

/**
 * How long after the answer is read its Resource Timing entry may take to come, in milliseconds, before the probe
 * reports no timing.
 */
export const ENTRY_WAIT_MS = 1000;

/**
 * The probe for a gateway: each call makes one request and reports its round trip once, or null when the request
 * failed, was aborted, was answered other than 204 (a limit's 429, a gateway without `/ping`), or came with no
 * usable timing (no `Timing-Allow-Origin` naming the page gives stamps of 0).
 *
 * @param origin The gateway's HTTP origin (`authBase` of its socket URL), with no trailing slash.
 * @returns The probe.
 * @example
 * ```ts
 * chatStore.bindProbe(httpProbe(authBase("wss://game.odysseytales.net/game")));
 * ```
 */
export function httpProbe(origin: string): PingProbe {
  let count = 0;
  return (report) => {
    count += 1;
    return probeOnce(new URL(`/ping?n=${count}`, origin).href, report);
  };
}

/**
 * Makes one request and reports its round trip once.
 *
 * @param address The request's full address, unique to the page.
 * @param report Told the round trip, or null for none.
 * @returns The abort, after which nothing is reported.
 */
function probeOnce(address: string, report: (ms: number | null) => void): () => void {
  if (typeof PerformanceObserver === "undefined" || typeof fetch === "undefined") {
    report(null);
    return () => undefined;
  }
  const controller = new AbortController();
  let settled = false;
  let answered = false;
  let entry: ResourceStamps | null = null;
  let wait: ReturnType<typeof setTimeout> | null = null;
  const observer = new PerformanceObserver((list) => {
    const found = list.getEntries().find((candidate) => candidate.name === address) as PerformanceResourceTiming | undefined;
    if (found !== undefined) {
      entry = { requestStart: found.requestStart, responseStart: found.responseStart };
      ready();
    }
  });

  /**
   * Stops watching and waiting.
   */
  function stop(): void {
    settled = true;
    observer.disconnect();
    if (wait !== null) {
      clearTimeout(wait);
      wait = null;
    }
  }

  /**
   * Reports once and stops.
   *
   * @param ms The round trip, or null for none.
   */
  function finish(ms: number | null): void {
    if (settled) {
      return;
    }
    stop();
    report(ms);
  }

  /**
   * Reports once both the answer has been read and its entry has come.
   */
  function ready(): void {
    if (answered && entry !== null) {
      finish(networkRoundTrip(entry));
    }
  }

  try {
    observer.observe({ type: "resource", buffered: true });
  } catch {
    stop();
    report(null);
    return () => undefined;
  }
  void fetch(address, { cache: "no-store", credentials: "omit", mode: "cors", signal: controller.signal })
    .then(async (response) => {
      await response.arrayBuffer();
      if (response.status !== 204) {
        finish(null);
        return;
      }
      answered = true;
      ready();
      if (!settled) {
        wait = setTimeout(() => finish(null), ENTRY_WAIT_MS);
      }
    })
    .catch(() => finish(null));
  return () => {
    if (!settled) {
      stop();
      controller.abort();
    }
  };
}
